import React from 'react';
import { DiabetesPageTemplate } from './DiabetesPageTemplate';
import { diabetesImages } from './diabetesPageData';

const DiabetesTreatment = () => {
  return (
    <DiabetesPageTemplate
      eyebrow="Care & Management"
      title="Diabetes Treatment"
      intro="Care and treatment for diabetes focus on bringing blood sugar close to the normal range using lifestyle measures, medication, and self-monitoring. Good control lowers the risk of eye, kidney, nerve, and foot complications."
      heroSummary="The exact plan depends on whether the person has Type 1, Type 2, or gestational diabetes, but all care plans rely on regular monitoring and steady follow-up."
      heroImage={diabetesImages.img4}
      heroAlt="Diabetes treatment planning and medical care"
      sections={[
        {
          title: 'Core Pillars Of Diabetes Care',
          columns: [
            {
              title: 'Lifestyle: Diet And Exercise',
              items: [
                'Balanced nutrition and portion control',
                'Regular physical activity such as 150 minutes per week',
                'Weight management',
              ],
            },
            {
              title: 'Medication',
              items: [
                'Insulin therapy for Type 1 and advanced Type 2 diabetes',
                'Oral anti-diabetic drugs',
                'Combination regimens when needed',
              ],
            },
            {
              title: 'Self-Monitoring Of Glucose',
              items: [
                'Daily fingerstick checks or CGM',
                'Work toward target HbA1c levels',
                'Prevent hypoglycemia and hyperglycemia',
              ],
            },
          ],
          image: diabetesImages.effectImg,
          imageAlt: 'Long-term diabetes effects and prevention',
          imageCaption: 'Effective treatment is not just about sugar control, but about preventing organ damage over time.',
        },
        {
          title: 'Type 1 Diabetes Treatment',
          paragraphs: [
            'Key strategy: External insulin supplementation because the pancreas stops producing insulin. Insulin is a protein that would be degraded by stomach enzymes, so it must be injected subcutaneously.',
          ],
          columns: [
            {
              title: 'Types Of Insulin And Delivery',
              items: [
                'Rapid-acting for mealtime coverage',
                'Short-acting or regular insulin',
                'Intermediate-acting insulin such as NPH',
                'Long-acting basal insulin such as glargine or detemir',
                'Delivery through syringes, insulin pens, or insulin pumps',
              ],
            },
            {
              title: 'Other Essential Factors',
              items: [
                'Regular glucose monitoring, often four or more times a day as advised',
                'Balanced diet with carbohydrate intake spread throughout the day',
                'Physical activity with attention to hypoglycemia and hyperglycemia risk',
                'Yearly checks for eyes, kidneys, heart, nerves, and dental problems',
                'Avoid smoking and alcohol',
              ],
            },
          ],
        },
        {
          title: 'Type 2 Diabetes Treatment',
          paragraphs: [
            'For newly diagnosed Type 2 diabetes, treatment focuses on normalizing blood glucose and evaluating complications such as kidney, eye, heart, and nerve disease that may already be present.',
          ],
          columns: [
            {
              title: 'Initial Step: Lifestyle Modification',
              items: [
                'Healthy and balanced diet',
                'Lose excess weight',
                'Regular physical activity',
              ],
            },
            {
              title: 'Medication Escalation If Needed',
              items: [
                'Lifestyle modification plus one anti-diabetic pill such as metformin',
                'Lifestyle modification plus more than one anti-diabetic pill',
                'Lifestyle modification plus two or more kinds of anti-diabetic pills',
                'Lifestyle modification plus anti-diabetic pills and insulin injection',
              ],
            },
            {
              title: 'Other Components Of Management',
              items: [
                'Check glucose and HbA1c every 2 to 6 months until stable, then at least twice a year',
                'Blood pressure checked at every visit',
                'Annual checks for eyes, kidneys, heart, nerves, and feet',
                'Dental cleaning and check-ups twice a year',
                'Keep cholesterol and blood pressure within normal range',
                'Avoid smoking',
                'Stay consistent with medicines and lifestyle changes',
                'Use proper foot and skin care with daily inspection and moisturizing',
              ],
            },
          ],
          image: diabetesImages.checkupImg,
          imageAlt: 'Diabetes check-up and complication prevention',
          imageCaption: 'Type 2 diabetes treatment usually expands gradually based on blood sugar response and complications.',
        },
        {
          title: 'Gestational Diabetes Treatment',
          paragraphs: [
            'Gestational diabetes is successfully managed in most cases by regular physical activity and following nutritional recommendations as advised by the doctor. Some women may require medication such as insulin or oral agents for effective blood sugar control to ensure maternal and fetal well-being.',
          ],
          items: [
            'Frequent glucose monitoring during pregnancy',
            'Personalized meal planning with controlled carbohydrates',
            'Safe exercise routines such as walking and prenatal workouts',
            'If needed: insulin therapy or metformin',
            'Postpartum follow-up to assess diabetes risk',
          ],
          note: 'Always consult your healthcare team for an individualized treatment plan. Consistent monitoring, medication adherence, and lifestyle changes are key to preventing long-term complications.',
        },
      ]}
    />
  );
};

export default DiabetesTreatment;
